import React, { useState } from 'react';
import { Sparkles, Heart, BookOpen, Star, HelpCircle } from 'lucide-react';

export type MascotSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero' | 'custom';
export type MascotPosition = 'inline' | 'bottom-right' | 'bottom-left' | 'top-right' | 'hero';
export type MascotExpression = 'happy' | 'encouraging' | 'celebrating' | 'thinking' | 'surprised';
export type MascotAnimation = 'none' | 'floating' | 'waving' | 'celebrating';
export type MascotVariant = 'full' | 'avatar';

export interface QuickBibleMascotProps {
  /** Size tier of the mascot */
  size?: MascotSize;
  /** Layout positioning mode */
  position?: MascotPosition;
  /** Emotional expression & thematic aura */
  expression?: MascotExpression;
  /** Idle/active CSS animation mode */
  animation?: MascotAnimation;
  /** Visibility toggle (defaults to true) */
  visibility?: boolean;
  /** Whether to use full-body artwork or square portrait avatar */
  variant?: MascotVariant;
  /** Optional speech bubble dialogue or Scripture encouragement */
  speechBubbleText?: string;
  /** Placement of speech bubble relative to character */
  speechBubblePosition?: 'top' | 'left' | 'right' | 'bottom';
  /** Optional mini badge text placed near mascot */
  badgeText?: string;
  /** Whether to render the warm golden scripture halo glow behind the Bible */
  showGlow?: boolean;
  /** Whether to apply a smooth bottom black gradient mask fade */
  maskBottomFade?: boolean;
  /** Optional custom CSS classes applied to root wrapper */
  className?: string;
  /** Optional custom CSS classes applied directly to the image */
  imageClassName?: string;
  /** Optional click handler for interactive greetings/sounds */
  onClick?: () => void;
  /** Custom accessible alt label */
  alt?: string;
  /** Whether to prioritize loading (e.g. for hero above the fold) */
  priority?: boolean;
}

const DEFAULT_MASCOT_WEBP = '/assets/mascot/quickbible_mascot.webp';
const DEFAULT_MASCOT_PNG = '/assets/mascot/quickbible_mascot.png';
const AVATAR_MASCOT_WEBP = '/assets/mascot/quickbible_mascot_avatar.webp';
const AVATAR_MASCOT_PNG = '/assets/mascot/quickbible_mascot_avatar.png';

export const QuickBibleMascot: React.FC<QuickBibleMascotProps> = ({
  size = 'md',
  position = 'inline',
  expression = 'happy',
  animation = 'floating',
  visibility = true,
  variant = 'full',
  speechBubbleText,
  speechBubblePosition = 'top',
  badgeText,
  showGlow = true,
  maskBottomFade = false,
  className = '',
  imageClassName = '',
  onClick,
  alt = 'Quick!Bible Official Mascot — Faith, Wisdom and Encouragement',
  priority = false,
}) => {
  const [hasError, setHasError] = useState(false);
  const [bubbleDismissed, setBubbleDismissed] = useState(false);

  if (!visibility) return null;

  // Determine dimensions based on size & variant
  const sizeClasses = (() => {
    if (size === 'custom') return '';

    if (variant === 'avatar') {
      switch (size) {
        case 'xs':
          return 'w-8 h-8 rounded-full';
        case 'sm':
          return 'w-12 h-12 rounded-full';
        case 'md':
          return 'w-16 h-16 rounded-full';
        case 'lg':
          return 'w-24 h-24 rounded-2xl';
        case 'xl':
          return 'w-32 h-32 rounded-2xl';
        case 'hero':
          return 'w-40 h-40 rounded-3xl';
        default:
          return 'w-16 h-16 rounded-full';
      }
    }

    // Full figure artwork proportions (aspect ratio ~1.5 : 1)
    switch (size) {
      case 'xs':
        return 'w-12 h-10 sm:w-16 sm:h-12';
      case 'sm':
        return 'w-20 h-16 sm:w-24 sm:h-20';
      case 'md':
        return 'w-32 h-24 sm:w-44 sm:h-32';
      case 'lg':
        return 'w-52 h-40 sm:w-64 sm:h-48 md:w-72 md:h-56';
      case 'xl':
        return 'w-64 h-52 sm:w-80 sm:h-64 md:w-96 md:h-72';
      case 'hero':
        return 'w-full max-w-[340px] sm:max-w-[440px] md:max-w-[540px] lg:max-w-[620px] h-auto max-h-[300px] sm:max-h-[380px] md:max-h-[460px] lg:max-h-[520px]';
      default:
        return 'w-44 h-32';
    }
  })();

  // Positioning classes
  const positionClasses = (() => {
    switch (position) {
      case 'bottom-right':
        return 'fixed bottom-4 right-4 z-40 select-none';
      case 'bottom-left':
        return 'fixed bottom-4 left-4 z-40 select-none';
      case 'top-right':
        return 'fixed top-20 right-4 z-40 select-none';
      case 'hero':
        return 'relative w-full flex items-center justify-center select-none pointer-events-none sm:pointer-events-auto';
      case 'inline':
      default:
        return 'relative inline-flex items-center justify-center select-none';
    }
  })();

  // Animation classes
  const animationClass = (() => {
    switch (animation) {
      case 'floating':
        return 'animate-mascot-float';
      case 'waving':
        return 'animate-mascot-wave';
      case 'celebrating':
        return 'animate-mascot-celebrate';
      case 'none':
      default:
        return '';
    }
  })();

  // Expression glow color & accents
  const expressionAccents = (() => {
    switch (expression) {
      case 'celebrating':
        return {
          glowColor: 'bg-amber-400/25',
          ringColor: 'ring-amber-400/40',
          badgeBg: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
          Icon: Star,
        };
      case 'encouraging':
        return {
          glowColor: 'bg-orange-500/25',
          ringColor: 'ring-orange-500/40',
          badgeBg: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
          Icon: Heart,
        };
      case 'thinking':
        return {
          glowColor: 'bg-amber-300/20',
          ringColor: 'ring-amber-300/30',
          badgeBg: 'bg-slate-700/60 text-amber-200 border-amber-400/30',
          Icon: HelpCircle,
        };
      case 'surprised':
        return {
          glowColor: 'bg-amber-500/30',
          ringColor: 'ring-amber-400/50',
          badgeBg: 'bg-amber-500/25 text-amber-200 border-amber-400/40',
          Icon: Sparkles,
        };
      case 'happy':
      default:
        return {
          glowColor: 'bg-amber-500/20',
          ringColor: 'ring-amber-500/30',
          badgeBg: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
          Icon: BookOpen,
        };
    }
  })();

  // Speech bubble placement
  const bubblePlacementClass = (() => {
    switch (speechBubblePosition) {
      case 'left':
        return 'bottom-full sm:bottom-auto sm:right-full mb-3 sm:mb-0 sm:mr-3.5 left-1/2 sm:left-auto -translate-x-1/2 sm:translate-x-0 sm:top-1/2 sm:-translate-y-1/2';
      case 'right':
        return 'bottom-full sm:bottom-auto sm:left-full mb-3 sm:mb-0 sm:ml-3.5 left-1/2 sm:left-auto -translate-x-1/2 sm:translate-x-0 sm:top-1/2 sm:-translate-y-1/2';
      case 'bottom':
        return 'top-full mt-2.5 left-1/2 -translate-x-1/2';
      case 'top':
      default:
        return 'bottom-full mb-3 left-1/2 -translate-x-1/2';
    }
  })();

  const imageMaskStyle: React.CSSProperties = maskBottomFade
    ? {
        WebkitMaskImage:
          'linear-gradient(to bottom, black 0%, black 72%, rgba(0, 0, 0, 0.85) 86%, transparent 100%)',
        maskImage:
          'linear-gradient(to bottom, black 0%, black 72%, rgba(0, 0, 0, 0.85) 86%, transparent 100%)',
      }
    : {};

  const webpSrc = variant === 'avatar' ? AVATAR_MASCOT_WEBP : DEFAULT_MASCOT_WEBP;
  const pngSrc = variant === 'avatar' ? AVATAR_MASCOT_PNG : DEFAULT_MASCOT_PNG;

  return (
    <div
      data-testid="quickbible-mascot"
      className={`${positionClasses} ${className}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className={`relative flex items-center justify-center ${animationClass}`}>
        {/* Ambient Scripture Glow halo behind her glowing Bible and head */}
        {showGlow && (
          <div
            className={`absolute inset-0 rounded-full blur-2xl pointer-events-none transition-all duration-700 ${expressionAccents.glowColor} animate-mascot-aura`}
            style={{ transform: 'scale(1.2)' }}
            aria-hidden="true"
          />
        )}

        {/* Celebrating sparkle particles */}
        {expression === 'celebrating' && (
          <div className="absolute -top-3 -right-2 pointer-events-none flex items-center gap-1 z-20 animate-bounce">
            <Sparkles className="w-5 h-5 text-amber-300 fill-amber-300 drop-shadow" />
            <Star className="w-4 h-4 text-orange-400 fill-orange-400 drop-shadow" />
          </div>
        )}

        {/* Speech Bubble / Encouragement */}
        {speechBubbleText && !bubbleDismissed && (
          <div
            data-testid="mascot-speech-bubble"
            className={`absolute z-30 pointer-events-auto w-max max-w-[260px] sm:max-w-[320px] px-4 py-3 sm:px-4.5 sm:py-3.5 rounded-2xl bg-[#171717] border border-amber-500/40 text-[#FAFAFA] shadow-2xl shadow-black/80 backdrop-blur-md leading-snug animate-in fade-in zoom-in-95 duration-200 ${bubblePlacementClass}`}
            style={{ textWrap: 'balance' }}
          >
            <div className="flex items-center gap-2.5">
              <expressionAccents.Icon className="w-4 h-4 text-amber-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-[#FAFAFA] font-bold leading-snug break-normal">
                  {speechBubbleText}
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setBubbleDismissed(true);
                }}
                className="text-[#737373] hover:text-[#FAFAFA] text-xs ml-1 shrink-0 px-1 py-0.5 rounded cursor-pointer transition-colors"
                title="Dismiss encouragement"
                aria-label="Dismiss encouragement"
              >
                ✕
              </button>
            </div>
            {/* Tail pointing toward mascot */}
            <div
              className={`absolute w-2.5 h-2.5 bg-[#171717] border-amber-500/40 transform rotate-45 ${
                speechBubblePosition === 'top'
                  ? 'bottom-[-6px] left-1/2 -translate-x-1/2 border-r border-b'
                  : speechBubblePosition === 'bottom'
                  ? 'top-[-6px] left-1/2 -translate-x-1/2 border-l border-t'
                  : speechBubblePosition === 'left'
                  ? 'bottom-[-6px] left-1/2 -translate-x-1/2 border-r border-b sm:bottom-auto sm:top-1/2 sm:left-auto sm:right-[-6px] sm:-translate-y-1/2 sm:border-t sm:border-r sm:border-b-0 sm:border-l-0'
                  : 'left-[-6px] top-1/2 -translate-y-1/2 border-b border-l'
              }`}
            />
          </div>
        )}

        {/* Mascot Image with WebP + PNG Fallback */}
        {!hasError ? (
          <picture
            className={`relative z-10 flex items-center justify-center ${sizeClasses}`}
            style={imageMaskStyle}
          >
            <source srcSet={webpSrc} type="image/webp" />
            <img
              src={pngSrc}
              alt={alt}
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              referrerPolicy="no-referrer"
              onError={() => setHasError(true)}
              className={`object-contain drop-shadow-[0_8px_24px_rgba(0,0,0,0.45)] transition-transform duration-300 ${
                variant === 'avatar'
                  ? 'w-full h-full object-cover rounded-full border-2 border-amber-400/60 shadow-lg'
                  : 'w-full h-auto'
              } ${imageClassName}`}
            />
          </picture>
        ) : (
          /* Graceful CSS / SVG Fallback if image fails */
          <div
            className={`flex flex-col items-center justify-center rounded-2xl bg-gradient-to-br from-amber-950/40 via-[#1C1C1C] to-black border border-amber-500/30 text-amber-300 p-3 text-center ${sizeClasses}`}
            title="Quick!Bible Mascot"
          >
            <BookOpen className="w-7 h-7 text-amber-400 mb-1" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-200">
              Quick!Bible
            </span>
          </div>
        )}

        {/* Optional mini badge */}
        {badgeText && (
          <div
            className={`absolute -bottom-2 z-20 px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-wide uppercase border shadow-md flex items-center gap-1 ${expressionAccents.badgeBg}`}
          >
            <Sparkles className="w-2.5 h-2.5" />
            <span>{badgeText}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickBibleMascot;
